<template>
  <div>
  <v-autocomplete
    ref="autocomplete"
    :dense="dense"
    :label="label"
    :value="value"
    :search-input.sync="searchInput"
    @input="update($event)"
    :items="items"
    :item-text="itemText"
    :item-value="itemValue"
    :loading="loading"
    :error="error"
    :error-messages="errorMessages"
    :menu-props="menuProps"
    :return-object="returnObject"
    :disabled="disabled"
    color="primary"
    multiple
    deletable-chips
    clearable>
    <template v-slot:prepend-inner>
      <v-icon>{{ prependInnerIcon }}</v-icon>
    </template>
    <template v-slot:prepend-item v-if="all">
      <v-list-item @click="selectAll">
        <v-list-item-content>
          <v-list-item-title>
            Select All
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:selection="data">
      <v-chip
        :dense="dense"
        :small="small"
        :disabled="disabled"
        :class="get(data.item, 'class', 'white--text')"
        :color="get(data.item, 'color', color)"
        v-bind="data.attrs"
        :input-value="data.selected"
        close
        @click="data.select"
        @click:close="removeItem(data.item)">
        <v-avatar left v-if="data.item.icon">
          <v-icon>{{ data.item.icon }}</v-icon>
        </v-avatar>
        {{ data.item[itemText] }}
      </v-chip>
    </template>
  </v-autocomplete>
  </div>
</template>

<script>
import { get, remove, clone } from 'lodash'

/**
 * Wrapper around v-autocomplete with what should be our conventional configuration for a multi-picker with search
 * and chips.
 */
export default {
  name: 'VfAutocomplete',
  props: {
    label: {
      type: String
    },
    value: {
      type: Array,
      default: () => []
    },
    /**
     * The color to use. Affects the highlighted color as well as the chip color.
     */
    color: {
      type: String,
      default: 'primary'
    },
    /**
     * Pass-through for the 'items' object.
     */
    items: {
      type: Array,
      default: () => []
    },
    /**
     * Pass-through for 'dense' property
     */
    dense: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through for 'filled' property
     */
    filled: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through for 'small' property
     */
    small: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through for 'prependInnerIcon' property
     */
    prependInnerIcon: {
      type: String
    },
    /**
     * Pass-through for 'loading' property
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through property for the 'error' property (delegates to the text box)
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through property for the 'error-messages' property (delegates to the text box)
     */
    errorMessages: {
      default: () => []
    },
    /**
     * Pass-through property for the 'item-text' property on v-autocomplete
     */
    itemText: {
      default: 'text'
    },
    /**
     * Pass-through property for the 'item-value' property on v-autocomplete.
     */
    itemValue: {
      default: 'value'
    },
    /**
     * Pass-through property for the 'menuProps' property on v-autocomplete.
     */
    menuProps: {
      default: ''
    },
    /**
     * Pass-through property for the 'returnObject' property on v-autocomplete.
     */
    returnObject: {
      default: false
    },
    disabled: {
      default: false
    },
    all: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      searchInput: null
    }
  },
  methods: {
    get: get,
    update (value) {
      this.searchInput = null
      this.$emit('input', value)
    },
    /**
     * Remove an item from selection.
     * @param item
     */
    removeItem (item) {
      // Since the lodash `remove` function mutates the array prop, we need to clone the array and then emit the updated
      // value afterwards to avoid directly mutating the prop.
      const tempArray = clone(this.value)
      const lookupValue = get(item, this.itemValue)
      remove(tempArray, el => {
        // If an object, examine for the appropriate key
        if (typeof el === 'object') {
          return get(el, this.itemValue) === lookupValue
        }
        // Or just compare directly.
        return el === lookupValue
      })
      this.update(tempArray)
    },
    selectAll () {
      const itemValues = this.items.map(item => item[this.itemValue])
      this.update(itemValues)
      this.$nextTick(() => {
        if (this.$refs.autocomplete) {
          const autocomplete = this.$refs.autocomplete
          // Programmatically blur then focus the autocomplete to close the dropdown
          autocomplete.blur()
          this.$nextTick(() => {
            autocomplete.focus()
          })
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
